/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState, useReducer } from 'react';
/* eslint-disable no-inner-declarations */
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { SearchFormProps } from './NGSearchForm.types';
import { StyledSearchForm, StyledSearchFormWrapper } from './NGSearchForm.styles';
import { useCheckBreakpoint } from '../../hooks/lib/useCheckBreakpoint';
import { useSearchFormStore } from '../../modules/store/searchFormStore';
import { SPECIAL_RATES, USE_POINTS } from '../../modules/store/store.constants';
import { SearchFormView } from './NGSearchForm.controller';
import clsx from 'clsx';
import { StaticDataContextProvider } from '../../modules/context';
import { SERP_VALIDATE_URL } from '../../modules/constants';
/* eslint-disable @nx/enforce-module-boundaries */
import { getSessionData } from '@marriott/mi-headless-utils';
import { usePersistentGlobalStore, useClientEnvVarsStore, useDCAPageModelStore } from '@marriott/mi-store-utils';
import { logger } from '@marriott/mi-headless-utils';
/* eslint-enable @nx/enforce-module-boundaries */
import { EditableComponent } from '@adobe/aem-react-editable-components';
import React from 'react';
import { ADF_SEARCH_CTA_ENDPOINT_URL } from '../../constants/Calendar.constant';
import { useNGSearchFormScroll } from '../../hooks/lib/useNGSearchFormScroll';
import { HQVWrapper } from '../HQVWrapper';
import { HQVContext } from '../../context/HQVContext';
import { hqvInitialState, hqvReducer } from '../../reducers/HQV';
import { useHQVStore } from '../../modules/store/hqvZustandStore';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import {
  constructSearchformLabels,
  isConcurFlowActive,
  extractConcurCode,
  getSpecialRatesForSearch,
  hasConcurUserOverride,
  clearConcurUserOverride,
} from '../../modules/utils/searchFormHelper';
import { useGetUserInfo } from '../../hooks/lib/useGetUserInfo';
import { enableConversationalSearch, IUpdatedPageModel } from '../../modules/utils/searchABTestUtil';
import { useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { FLAG_TYPE } from '@marriott/mi-feature-flag-utils';

if (global && !global.loggerInstance) {
  global.loggerInstance = logger({ requestID: '', sessionID: '' }) as CallableFunction;
}

const NG_SEARCHFORM_SESSION_FETCH_KEY = '__miShopNGSearchFormSessionFetched';
const NG_SEARCHFORM_CONVO_AI_CACHE_KEY = '__miShopNGSearchFormConvoAICache';

type ConvoAISearchResult = ReturnType<typeof enableConversationalSearch>;

const defaultConvoAIResult: ConvoAISearchResult = {
  enableConvoAI: false,
  activityName: undefined,
  experienceName: undefined,
};

const getTargetConvoAIMbox = (mboxData?: IUpdatedPageModel['mboxData']) =>
  mboxData?.find(component => component?.mboxName === 'target-convoAI-mbox');

const readNGConvoAICache = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return (window as Window & { [NG_SEARCHFORM_CONVO_AI_CACHE_KEY]?: ConvoAISearchResult })[
    NG_SEARCHFORM_CONVO_AI_CACHE_KEY
  ];
};

const writeNGConvoAICache = (value: ConvoAISearchResult) => {
  if (typeof window === 'undefined') {
    return;
  }

  (window as Window & { [NG_SEARCHFORM_CONVO_AI_CACHE_KEY]?: ConvoAISearchResult })[NG_SEARCHFORM_CONVO_AI_CACHE_KEY] =
    value;
};

/**
 * Refer /libs/mi-shop-components/Readme.md for details of configuration variables for the Searchform
 * model ={<An AEM model with all author value>}
 * @param props
 * @returns
 */
// Use named rather than default exports.
export const NGSearchForm: FC<SearchFormProps> = React.memo(props => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _searchFormOnHomepage = props?.model?.['enableOverlayTop']; // this is the identifier which tells if the searchform is on HP or any other page
  const { model, isPositionedExternally, isSticky, isNonStickyVariant, refCallback } = props;
  // Detect if this is NextGenHP variant
  const isNextGenHP = props?.model?.['styleclass'] === 'nextGenHP';
  const isDesktopView = useCheckBreakpoint('viewportL');
  /**
   * constructSearchformLabels function maps few labels to work with common react code ,
   * so we do not have to maintain 2 different labels for single component even if AEM
   * is sending two different label names for Advanced search form and Home page search form.
   */
  constructSearchformLabels(model);
  const setSearchFormState = useSearchFormStore((state: any) => state.setSearchFormState); // function to update search form data in store

  const callGetSessionData =
    props?.model?.numberOfSearchForms ||
    props?.model?.enableOverlayTop ||
    props?.model?.searchCTAEndpointURL === ADF_SEARCH_CTA_ENDPOINT_URL ||
    props?.model?.searchCTAEndpointURL === SERP_VALIDATE_URL
      ? true
      : false; // flag to determine if get session call is required.
  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData); // function to update global data store
  const globalData = usePersistentGlobalStore((state: any) => state.globalData); // global data extracted from global data store
  /**
   * shop store to access the data
   */
  const shopStoreInitialization = useSearchFormStore((state: any) => state.shopStoreInitialization);
  const [isStoreInitialized, setIsStoreInitialized] = useState(false);
  const [currentLocale] = useState(props.currentLocale);

  /**
   * DCA (Digital Content Assembly) mbox data from Adobe Target.
   * Subscribing only to mboxData avoids rerenders from unrelated pageModel changes.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dcaMboxData = useDCAPageModelStore((state: any) => state.pageModel?.mboxData) as
    | IUpdatedPageModel['mboxData']
    | undefined;

  /**
   * HQV reducer state - only initialize when hideDestination is true
   * This provides the context for HQV components when needed
   */
  const [hqvState, hqvDispatch] = useReducer(hqvReducer, hqvInitialState);

  // Resolve convoAI experiment assignment from Adobe Target mbox using memoized derived values
  const convoAICache = readNGConvoAICache();
  const [convoAIResult, setConvoAIResult] = useState<ConvoAISearchResult>(convoAICache ?? defaultConvoAIResult);
  const isConvoAIProcessedRef = useRef(!!convoAICache);

  useEffect(() => {
    const isConvoAIEligible = isNextGenHP && model.enableConvoAISearch;

    if (!isConvoAIEligible) {
      setConvoAIResult(defaultConvoAIResult);
      return;
    }

    if (isConvoAIProcessedRef.current) {
      return;
    }

    const targetConvoAIMbox = getTargetConvoAIMbox(dcaMboxData);
    if (!targetConvoAIMbox?.uxlResponse) {
      return;
    }

    const evaluatedConvoAIResult = enableConversationalSearch({ mboxData: dcaMboxData } as IUpdatedPageModel);
    isConvoAIProcessedRef.current = true;
    setConvoAIResult(evaluatedConvoAIResult);
    writeNGConvoAICache(evaluatedConvoAIResult);
  }, [isNextGenHP, model.enableConvoAISearch, dcaMboxData]);

  const { enableConvoAI: enableConversationalAISearch, activityName, experienceName } = convoAIResult;

  /**
   * import search from component with modification
   * and state implemention
   */

  useEffect(() => {
    // Initialize the zustand store with the sessionData available in props
    if (!callGetSessionData) {
      // Skip reinitialization if Concur code was applied
      // to prevent overwriting Concur values with session data
      const featureFlagsLocal = envVarsObject?.['featureFlags'] as unknown as Record<string, any> | undefined;
      const sessionDoc = globalData?.sessionData?.data || globalData?.sessionData?.cacheData?.data;
      if (isConcurFlowActive(featureFlagsLocal, sessionDoc) && concurCodeApplied.current) {
        return;
      }

      shopStoreInitialization(globalData?.sessionData, model, !isNextGenHP && !props.hideDestination); // update recent searches only if its SERP (not HP and not ADF)
      setIsStoreInitialized(true);
    }
  }, [globalData]);

  const { envVarsObject } = useClientEnvVarsStore();
  const requestId = envVarsObject?.['REQUEST_ID'];
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;
  const userDetailsState = useUserDetailsGlobalStore();
  const concurCodeApplied = useRef<boolean>(false);

  /**
   * Initialize HQV Zustand store when feature flag is enabled
   * This runs once on mount when hideDestination is true
   */
  useLayoutEffect(() => {
    const shouldUseZustand = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

    if (shouldUseZustand && props.hideDestination) {
      useHQVStore.getState().hqvInitialization(hqvInitialState);
    }
  }, [props.hideDestination, featureFlags]);

  // Always call the hook - it handles conditional logic internally
  const globalSessionDoc = globalData?.sessionData?.data || globalData?.sessionData?.cacheData?.data;
  useGetUserInfo({
    sessionToken: isConcurFlowActive(featureFlags, globalSessionDoc)
      ? globalData?.sessionData?.sessionToken
      : undefined,
    requestId,
    currentLocale,
  });

  // Update search form state with Concur code when user details are loaded
  useEffect(() => {
    if (!isStoreInitialized) return;
    if (!isConcurFlowActive(featureFlags, globalSessionDoc)) return;
    if (!userDetailsState?.isUserDetailsSaved) return;

    // Only apply Concur code once per session
    if (concurCodeApplied.current) {
      return;
    }

    // Check if user has manually overridden Concur in this session
    if (hasConcurUserOverride()) {
      concurCodeApplied.current = true; // Mark as handled to prevent reprocessing
      return;
    }

    const currentState = useSearchFormStore.getState();
    const concurCode = extractConcurCode(userDetailsState); // Extract on-the-fly

    const resolved = getSpecialRatesForSearch(
      currentState.specialRates?.specialRateCode, // Zustand uses specialRateCode
      currentState.specialRates?.corporateCode,
      concurCode,
      currentState.usePoints?.rewardsRedemption,
      model?.specialRatesListItems || []
    );

    // Update if needed - Zustand store structure
    if (
      currentState.specialRates?.clusterCode !== resolved.clusterCode ||
      currentState.specialRates?.specialRateCode !== resolved.specialRateCode ||
      currentState.specialRates?.corporateCode !== resolved.corporateCode
    ) {
      setSearchFormState([SPECIAL_RATES], {
        [SPECIAL_RATES]: {
          clusterCode: resolved.clusterCode, // Label (e.g., "Corp/Promo Code")
          specialRateCode: resolved.specialRateCode, // Code (e.g., 'corp')
          corporateCode: resolved.corporateCode, // Actual corporate code value
        },
      });
      // Mark that Concur was applied
      concurCodeApplied.current = true;
    }
  }, [userDetailsState?.isUserDetailsSaved, featureFlags, globalData?.sessionData?.authenticated, isStoreInitialized]);

  // Clear override flag on sign-out
  useEffect(() => {
    if (globalData?.sessionData?.authenticated === false) {
      clearConcurUserOverride();
    }
  }, [globalData?.sessionData?.authenticated]);

  // Reset Concur flag when form is submitted or user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      concurCodeApplied.current = false;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // No cleanup needed on sign-out - userDetailsGlobalStore.resetUserInfo() already clears customerAffiliates

  const setSearchFormTopPosition = () => {
    const offsetVal = 24;
    let searchFormTopPositionValue = searchFormPositionWrapperRef?.current?.getBoundingClientRect()?.top;
    if (searchFormTopPositionValue && searchFormTopPositionValue < offsetVal) {
      searchFormTopPositionValue = 0;
    }
    if (searchFormTopPositionValue) {
      setSearchFormState(['searchFormTopPosition'], {
        searchFormTopPosition: {
          value: searchFormTopPositionValue,
        },
      });
    } else {
      setSearchFormState(['searchFormTopPosition'], {
        searchFormTopPosition: {
          value: window.scrollY,
        },
      });
    }
  };
  useEffect(() => {
    setSearchFormTopPosition();
    async function getSession() {
      // Extract the sessionID cookie value from browser cookies
      const sessionID = document?.cookie?.split('sessionID=')[1]?.split(';')[0];
      const isLocalDev = process.env['IS_LOCAL_DEV'] === 'true';
      // Make the client side GET session call
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await getSessionData(sessionID, isLocalDev, false, requestId);

      // Check if Concur code was applied while we were fetching
      const featureFlagsLocal = envVarsObject?.['featureFlags'] as unknown as Record<string, any> | undefined;
      const responseSessionDoc = response?.sessionData?.data || response?.sessionData?.cacheData?.data;
      if (!isConcurFlowActive(featureFlagsLocal, responseSessionDoc) || !concurCodeApplied.current) {
        // Only initialize store if Concur wasn't applied during fetch
        shopStoreInitialization(response?.sessionData, model, !isNextGenHP && !props.hideDestination); // update recent searches only if its SERP (not HP and not ADF)
        setIsStoreInitialized(true);
      }

      // Always update globalData so other components get the session
      updateGlobalData('sessionData', response?.sessionData);
    }
    // make the client side session call from SearchForm component
    if (callGetSessionData && typeof window !== 'undefined') {
      const windowWithSessionFetchFlag = window as Window & {
        [NG_SEARCHFORM_SESSION_FETCH_KEY]?: boolean;
      };

      if (!windowWithSessionFetchFlag[NG_SEARCHFORM_SESSION_FETCH_KEY]) {
        windowWithSessionFetchFlag[NG_SEARCHFORM_SESSION_FETCH_KEY] = true;
        getSession();
      }
    }
  }, []);

  const searchFormContainerElRef = useRef<HTMLDivElement>(null);
  const searchFormContainerRef = useRef<HTMLDivElement>(null);
  const searchFormPositionWrapperRef = useRef<HTMLDivElement>(null);

  // Custom scroll hook for NGSearchForm - only enabled when isNextGenHP is false
  const { scrollState, searchFormRef } = useNGSearchFormScroll({
    enabled: !isNextGenHP || !isPositionedExternally ? true : false,
    gapBetweenHeaderAndForm: isNextGenHP ? 24 : 0,
    searchFormPositionStyle: isNextGenHP ? 'absolute' : 'relative',
  });

  // Callback to handle both refs
  const handleSearchFormRef = (el: HTMLDivElement | null) => {
    // Update the original ref
    if (searchFormContainerElRef.current !== el) {
      (searchFormContainerElRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    }
    // Update the scroll hook ref
    searchFormRef(el);
  };

  useEffect(() => {
    if (isPositionedExternally) {
      if (refCallback && searchFormContainerRef.current) {
        refCallback(
          searchFormContainerRef.current,
          searchFormContainerRef.current?.getBoundingClientRect().top -
            (searchFormContainerElRef?.current?.getBoundingClientRect().top || 0)
        );
      }
      return;
    }
  }, []);

  const staticDataContextValue = useMemo(() => ({ ...model, currentLocale }), [model, currentLocale]);

  const searchFormContent = (
    <StyledSearchForm
      ref={!isNextGenHP || !isPositionedExternally ? undefined : searchFormContainerElRef}
      $isNextGenHP={isNextGenHP}
      $hideDestination={props.hideDestination}
      className={clsx('d-flex', 'document_search_form_container_el_ref', 'pt-0 pb-0', {
        'px-3': !isDesktopView && isNextGenHP,
        'pt-lg-5': isNextGenHP,
        'search-wrapped': isPositionedExternally,
        'sticky-search-wrapped color-scheme1': isPositionedExternally && isSticky,
        // Apply sticky styles when scroll hook determines form should be sticky
        'ng-sticky-form color-scheme1': (!isNextGenHP || !isPositionedExternally) && scrollState.isSticky,
      })}
    >
      <StaticDataContextProvider value={staticDataContextValue}>
        <div
          ref={searchFormContainerRef}
          className={clsx(
            // Both NextGen and NextGenHP use Bootstrap container for 1140px max-width
            'container',
            // NextGen: No padding on mobile (full bleed), padding on desktop
            // NextGenHP: No horizontal padding (original)
            'px-0',
            'search-form-wrapper',
            'document_search_form_container',
            enableConversationalAISearch && !isDesktopView ? 'enable-conversational-ai-search' : '',
            {
              'color-scheme1': !isPositionedExternally || !isSticky,
            }
          )}
        >
          <SearchFormView
            {...{
              sessionData: globalData,
              model,
              currentLocale,
              hideBrandsField: props.hideBrandsField,
              hideDestination: props.hideDestination,
              isNonStickyVariant: isNonStickyVariant,
              destinationColStyleOverride: props.destinationColStyleOverride,
              isNextGenHP,
              isSticky: isSticky,
              enableConversationalAISearch: enableConversationalAISearch,
            }}
          ></SearchFormView>
        </div>
      </StaticDataContextProvider>
    </StyledSearchForm>
  );

  /**
   * TODO: All store calculation will be done as part of this file
   * We will pass proccess data to useSEarchForm in the form of props
   */

  // Extract the wrapper content for conditional rendering
  const wrapperContent = (
    <HQVWrapper currentLocale={currentLocale || 'en-US'} globalData={globalData} model={model}>
      <StyledSearchFormWrapper
        data-component-name="ng-shop-searchform"
        data-testid="searchform"
        data-test={activityName && experienceName ? 'true' : undefined}
        data-experiment-name={activityName}
        data-variant={experienceName}
        ref={!isNextGenHP || !isPositionedExternally ? handleSearchFormRef : undefined}
        className={clsx(
          // NextGen: Full bleed (full width of browser)
          // NextGenHP: Only full bleed if no styleclass
          !isNextGenHP ? 'm-container-fullbleed' : props?.model?.['styleclass'] ? '' : 'm-container-fullbleed',
          isNextGenHP ? 'ng-search-form-position' : ''
        )}
        isNonStickyVariant={isNonStickyVariant}
        $isNextGenHP={isNextGenHP}
      >
        {searchFormContent}
      </StyledSearchFormWrapper>
    </HQVWrapper>
  );

  // Conditionally wrap with HQVContext.Provider based on hideDestination prop
  return props.hideDestination ? (
    <HQVContext.Provider value={{ hqvState, hqvDispatch }}>{wrapperContent}</HQVContext.Provider>
  ) : (
    wrapperContent
  );
});

// Config for aem editable components
export const NGSearchFormConfig = {
  emptyLabel: 'NGSearchForm',
  isEmpty: false,
  resourceType: `mi-aem-shop-spa/components/content/homepagesearch/v1/homepagesearch`,
};
// Component wrapped with editable component for aem authoring
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const NGSearchFormEditable = (props: any) =>
  props.cqPath?.includes('experiencefragment') ? (
    <NGSearchForm {...props} />
  ) : (
    <EditableComponent config={NGSearchFormConfig} {...props}>
      <NGSearchForm {...props} />
    </EditableComponent>
  );
